
import { zodResolver } from '@hookform/resolvers/zod';
import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { TokenTypeCode } from '@/services/types';
import {
  ResetPasswordFormValues,
  resetPasswordFormSchema,
} from '@/services/auth/form-schemas';
import { useResetPasswordComplete } from '@/services/auth/mutations';
import { ServiceErrorCode } from '@/services/utils/service-error';

import {
  AuthTemplate,
  FooterLinks,
} from '@/pages/unauthenticated/auth/components';

import { useProcessError } from './hooks/use-process-error';
import { ResetPasswordCompleteSuccess } from './reset-password-complete-success';
import { ResetPasswordForm } from './reset-password-form';

export const ResetPassword = ({
  token,
  email,
}: {
  token: string;
  email: string;
}) => {
  const navigate = useNavigate();
  const processError = useProcessError();
  const [success, setSuccess] = useState(false);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordFormSchema),
    defaultValues: {
      token,
      email,
      password: '',
      organizationId: -1,
    },
  });

  const { mutate, isPending } = useResetPasswordComplete({
    onSuccess: () => setSuccess(true),
    onError: (error) => {
      if (error.errorCode === ServiceErrorCode.InvalidToken) {
        const params = new URLSearchParams({
          email,
          token,
          type: TokenTypeCode.RES_PWD,
          errorCode: ServiceErrorCode.InvalidToken,
        }).toString();
        navigate(`/verify/error?${params}`);
        return;
      }
      processError(form, { email, token }, {
        code: error.errorCode,
        validationErrorMap: error.validationErrorMap,
      });
    },
  });

  const onSubmit = form.handleSubmit((data) => mutate(data));

  const heading = success ? 'Password changed' : 'Reset your password';
  const subheading = success
    ? 'Login with your new password to access the dashboard.'
    : 'Enter your new password below.';

  return (
    <AuthTemplate
      heading={heading}
      subheading={subheading}
      footer={<FooterLinks />}
    >
      <AnimatePresence mode="wait">
        {!success ? (
          <ResetPasswordForm
            form={form}
            onSubmit={onSubmit}
            isPending={isPending}
          />
        ) : (
          <ResetPasswordCompleteSuccess />
        )}
      </AnimatePresence>
    </AuthTemplate>
  );
};
